import classnames from 'classnames';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/shared';
import {
  useListingSearchCriteria,
  ListingSearchCriteria,
} from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import FilterGroup from '@components/layouts/Truva/ListingSearchDeprecated/Header/Mobile/FilterGroup';
import useSticky from '@shared/useSticky';
import PossessionFilterList from '../PossessionFilter';
import CeilingHeightFilter from '../CeilingHeightFilter';
import SpaceConditionsFilter from '../SpaceConditionsFilter';
import TermsFilter from '../TermsFilter';
import SizeFilters from '../SizeFilter';
import PriceFilter from '../PriceFilter';
import LeaseTypeFilter from '../LeaseTypeFilter/LeaseTypeFilter';
import KeywordsFilter from '../KeywordsFilter/KeywordsFilter';
import AmenitiesFilter from '../AmenitiesFilter/AmenitiesFilter';
import s from './AllFiltersOverlay.module.less';

type Props = {
  onClose: () => void;
  onClear: () => void;
};

const AllFilterOverlay = ({ onClose, onClear }: Props) => {
  const history = useHistory();
  const criteria = useListingSearchCriteria();

  const { t } = useTranslation('filters');

  const onChange = (passedCriteria: ListingSearchCriteria) => {
    history.push(passedCriteria.toUrl());
  };

  const debouncedOnChange = debounce((callbackFn: () => ListingSearchCriteria) => {
    const newCriteria = callbackFn();
    onChange(newCriteria);
  }, 500);

  const clearFilters = () => {
    onClear();
    criteria.removeAllFilters();
    history.push(criteria.toUrl());
  };

  const { isSticky } = useSticky('#site-navigation');

  return (
    <div
      data-testid="allFilterOverlay"
      className={classnames(s.container, isSticky && s.withStickyFilters)}
    >
      <ul className={s.body}>
        <FilterGroup title={t('price.title')}>
          <PriceFilter criteria={criteria} onChange={debouncedOnChange} type="all" />
        </FilterGroup>
        <FilterGroup title={t('size.title')}>
          <SizeFilters criteria={criteria} type="all" onChange={debouncedOnChange} />
        </FilterGroup>
        <FilterGroup title={t('spaceCondition.title')}>
          <SpaceConditionsFilter criteria={criteria} type="all" onChange={onChange} />
        </FilterGroup>
        <FilterGroup title={t('term.title')}>
          <TermsFilter onChange={onChange} criteria={criteria} type="all" />
        </FilterGroup>
        <FilterGroup title={t('possession.title')}>
          <PossessionFilterList onChange={onChange} criteria={criteria} type="all" />
        </FilterGroup>
        <FilterGroup title={t('leaseType.title')}>
          <LeaseTypeFilter onChange={onChange} criteria={criteria} type="all" />
        </FilterGroup>
        <FilterGroup title={t('ceilingHeight.title')}>
          <CeilingHeightFilter onChange={debouncedOnChange} criteria={criteria} type="all" />
        </FilterGroup>
        <FilterGroup title={t('keywords.title')}>
          <KeywordsFilter onChange={debouncedOnChange} criteria={criteria} type="all" />
        </FilterGroup>
        <FilterGroup title={t('amenitiesTitle')}>
          <AmenitiesFilter onChange={onChange} criteria={criteria} type="all" />
        </FilterGroup>
      </ul>
      <section className={s.footer}>
        <div data-testid="tabletButtons" className={s.tabletButtons}>
          <button type="button" className={s.linkStyledButton} onClick={clearFilters}>
            {t('clearAll')}
          </button>
          <Button size="large" onClick={onClose}>
            {t('close')}
          </Button>
        </div>
        <div data-testid="desktopButtons" className={s.buttons}>
          <button type="button" className={s.linkStyledButton} onClick={clearFilters}>
            {t('clearAllFilters')}
          </button>
          <Button size="large" onClick={onClose}>
            {t('closeFilters')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AllFilterOverlay;
